import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { API_BASE_URL, API_ENDPOINTS } from '../../config';
import { track, setUser } from '../../utils/analytics';
import '../../styles/auth.css';

interface SignUpForm {
  email: string;
  password: string;
  confirmPassword: string;
  privacyConfirmed: boolean;
  reffCode?: string;
}

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignUpForm>({
    email: '',
    password: '',
    confirmPassword: '',
    privacyConfirmed: false,
    reffCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'OfferFlow | Sign Up';
  }, []);

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const userUid = localStorage.getItem('user_uid');
        if (!token || !userUid) return;

        await api.get(API_ENDPOINTS.me);
        navigate('/welcome', { replace: true });
      } catch {
      }
    };

    checkExistingSession();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = (): boolean => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!formData.privacyConfirmed) {
      setError('You must accept the Privacy Policy');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const requestBody: any = {
        email: formData.email,
        pwd: formData.password,
      };

      if (formData.reffCode && formData.reffCode.trim() !== '') {
        requestBody.reffCode = formData.reffCode.trim();
      }

      const response = await fetch(`${API_BASE_URL}/v1/create-new-user`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        track('sign_up', { method: 'email' });

        try {
          const loginResponse = await fetch(`${API_BASE_URL}/v1/login`, {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: '',
              username: formData.email,
              password: formData.password,
              scope: '',
              client_id: '',
              client_secret: '',
            }),
          });

          if (!loginResponse.ok) throw new Error('auto-login failed');
          const data = await loginResponse.json();

          localStorage.setItem('user_uid', data.user_uid);
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('user', JSON.stringify({
            uid: data.user_uid,
            email: formData.email,
            first_name: '',
            last_name: '',
          }));

          setUser(data.user_uid);
          track('login', { method: 'email', source: 'signup_auto' });
          window.dispatchEvent(new Event('auth-token-set'));
          navigate('/welcome');
          return;
        } catch {
          navigate('/sign-in', {
            state: {
              message: 'Account created successfully! Please sign in with your credentials.',
              type: 'success'
            }
          });
          return;
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-left">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Create Account</h1>
            <p>Join us and start your journey</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="Enter your password (min. 8 characters)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="Confirm your password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="reffCode">Referral Code (Optional)</label>
              <input
                type="text"
                id="reffCode"
                name="reffCode"
                value={formData.reffCode}
                onChange={handleChange}
                placeholder="Enter referral code if you have one"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="privacyConfirmed"
                  checked={formData.privacyConfirmed}
                  onChange={handleChange}
                  required
                />
                <span className="checkbox-text">
                  I agree to the{' '}
                  <a 
                    href="https://offerflow.pro/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="privacy-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a 
                    href="https://offerflow.pro/terms-and-conditions" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="privacy-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                </span>
              </label>
            </div>

            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

        <div className="auth-footer">
          <p className="auth-footer-row">
            <span>Already have an account?</span>
            <Link to="/sign-in" className="auth-link">
              Sign in
            </Link>
          </p>
        </div>
        </div>
      </div>
      
      <div className="auth-right">
        <div className="auth-visual auth-visual--signup">
          <div className="auth-visual-icon">
            <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="60" cy="50" r="25" stroke="#d1d5db" strokeWidth="2"/>
              <path d="M35 85 C35 70, 45 60, 60 60 C75 60, 85 70, 85 85" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
              <path d="M60 30 L60 50 M50 40 L60 50 L70 40" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="25" y="25" width="70" height="70" rx="8" stroke="#e5e7eb" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>
          <div className="auth-visual-text">
            <h3>Start Your Journey</h3>
            <p>Create your account and begin managing your career</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
